import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import BusinessesTableStore from './BusinessesTableStore';
import { ParentTable, Status } from '@mq/voltron-parent';
import { Table, ActionsCell, ActionPlaceholder } from '@mq/voltron-table';
import { LoadingOverlay, ModalAlert, VSpacer, ErrorModal } from '@mqd/volt-base';
import AddNoteModal from '../add-note-modal/AddNoteModal';
import ChangeStatusModal from '../change-status-modal/ChangeStatusModal';
import s from './BusinessesTable.module.css';
import { filterOptions } from './constants';

class BusinessesTable extends ParentTable {
  constructor(props) {
    super(props);
    this.storeConstructor = BusinessesTableStore;
    this.actionsRenderer = this.actionsRenderer.bind(this);
    this.state = {
      activeStore: null,
      showAddNoteModal: false,
      showChangeStatusModal: false,
      showNoteNotAddedAlert: false,
      showNoteSuccessfullyAddedAlert: false,
      tableKey: 'unset',
      ...this.colEditState(props, this.columns()),
    };
  }

  addNote(store) {
    this.setState({ showAddNoteModal: true, activeStore: store });
  }

  changeStatus(store) {
    this.setState({ showChangeStatusModal: true, activeStore: store });
  }

  redirectToDetailsTab(store, tab) {
    const { handleRedirect } = this.props;
    this.setState({ activeStore: store });
    handleRedirect
      ? handleRedirect(store, tab)
      : console.error('Error: Parent component must pass in handleDirect prop.');
  }

  statusRenderer({ cellValue, row }) {
    return (
      <div>
        <Status status={cellValue} testId="businesses-table-status-pill" />
      </div>
    );
  }

  actionsRenderer({ row }) {
    return (
      <ActionsCell
        actions={[
          {
            text: 'Add Note',
            onClick: () => this.addNote(row),
          },
          {
            text: 'Change Status',
            onClick: () => this.changeStatus(row),
          },
        ]}
      />
    );
  }

  children(row) {
    const children = [
      'Business Details',
      'Children Users',
      'Transactions',
      'History',
      'Notes',
      'Transitions',
    ];
    return children.map((child) => {
      return {
        element: <div className={s.action}>{child}</div>,
        onClick: () => this.redirectToDetailsTab(row, child),
      };
    });
  }

  columns() {
    return [
      // main info
      {
        header: this.headerRenderer(),
        accessor: 'token',
        key: 'actions',
        renderer: this.actionsRenderer,
        noPadding: true,
        noResize: true,
        noRowClick: true,
        noSort: true,
        noEdit: true,
        primaryKey: true,
        deferRenderPlaceholder: () => <ActionPlaceholder width={32} />,
      },
      {
        header: 'Status',
        accessor: 'status',
        key: 'status',
        renderer: this.statusRenderer,
      },
      {
        header: 'Business Name Legal',
        accessor: 'business_name_legal',
        key: 'business_name_legal',
      },
      {
        header: 'Business Name DBA',
        accessor: 'business_name_dba',
        key: 'business_name_dba',
        hide: true,
      },
      {
        header: 'Primary Contact Name',
        accessor: 'primaryContactFullName',
        key: 'primaryContactFullName',
        hide: true,
      },
      {
        header: 'Address',
        accessor: 'officeLocationAddress',
        key: 'officeLocationAddress',
      },
      {
        header: 'City',
        accessor: 'officeLocationCity',
        key: 'officeLocationCity',
      },
      {
        header: 'State',
        accessor: 'officeLocationState',
        key: 'officeLocationState',
      },
      {
        header: 'Postal Code',
        accessor: 'officeLocationPostalCode',
        key: 'officeLocationPostalCode',
      },
      {
        header: 'Last Modified',
        accessor: 'last_modified_time',
        key: 'last_modified_time',
      },
      {
        header: 'Token',
        accessor: 'token',
        key: 'token',
      },
      // // not shown
      { header: 'Type', accessor: 'business_type', key: 'business_type', hide: true },
      { header: 'Website', accessor: 'website', key: 'website', hide: true },
      { header: 'DUNS Number', accessor: 'duns_number', key: 'duns_number', hide: true },
      {
        header: 'Account Holder Group Token',
        accessor: 'account_holder_group_token',
        key: 'account_holder_group_token',
        hide: true,
      },
      {
        header: 'In Current Location Since',
        accessor: 'in_current_location_since',
        key: 'in_current_location_since',
        hide: true,
      },
      {
        header: 'Date Established',
        accessor: 'date_established',
        key: 'date_established',
        hide: true,
      },
      {
        header: 'General Business Description',
        accessor: 'general_business_description',
        key: 'general_business_description',
        hide: true,
      },
      { header: 'History', accessor: 'history', key: 'history', hide: true },
      {
        header: 'International Office Locations',
        accessor: 'international_office_locations',
        key: 'international_office_locations',
        hide: true,
      },
      { header: 'IP', accessor: 'ip_address', key: 'ip_address', hide: true },
      { header: 'Note', accessor: 'notes_string', key: 'notes_string', hide: true },
      // { header: 'SSN', accessor: 'ssn', key: 'ssn', hide: true },
      { header: 'Metadata', accessor: 'metadata', key: 'metadata', hide: true },
      { header: 'Password', accessor: 'password', key: 'password', hide: true },
    ];
  }

  renderAddNoteModal() {
    const { showAddNoteModal, activeStore } = this.state;
    const { userStore } = this.props;

    //TODO: Pull out userRole from userStore (current userRole is from Janus).
    //Map redseaUserRole to USER | ADMIN | BANK_USER | BANK_ADMIN | MARQETA_PD | MARQETA_ADMIN.
    //Pass in the correct created_by_user_role.
    //Temporarily, all notes will have created_by_user_role='ADMIN'.
    const { email } = userStore;
    const activeUserInfo = email
      ? {
          email,
          userRole: 'ADMIN',
        }
      : {};
    if (!showAddNoteModal) return null;
    return (
      <AddNoteModal
        testId="businesses-table__add-note-modal"
        hideModal={() => this.setState({ showAddNoteModal: false })}
        heading={`Add Note - ${activeStore.business_name_legal}`}
        store={activeStore}
        activeUserInfo={activeUserInfo}
        renderSuccessAlert={() => this.setState({ showNoteSuccessfullyAddedAlert: true })}
        renderFailedAlert={() => this.setState({ showNoteNotAddedAlert: true })}
      />
    );
  }

  renderChangeStatusModal() {
    const { showChangeStatusModal, activeStore } = this.state;
    if (!showChangeStatusModal) return null;
    return (
      <ChangeStatusModal
        testId="businesses-table__change-status-modal"
        hideModal={() => this.setState({ showChangeStatusModal: false })}
        heading={`Change Status - ${activeStore.business_name_legal}`}
        store={activeStore}
      />
    );
  }

  renderNoteSuccessfullyAddedAlert() {
    const { showNoteSuccessfullyAddedAlert } = this.state;
    if (!showNoteSuccessfullyAddedAlert) return null;
    return (
      <ModalAlert
        testId="businesses-table__note-success-modal-alert"
        type="success"
        title="Note successfully added"
        hideModal={() => this.setState({ showNoteSuccessfullyAddedAlert: false })}
      />
    );
  }

  renderNoteNotAddedAlert() {
    const { showNoteNotAddedAlert } = this.state;
    if (!showNoteNotAddedAlert) return null;
    return (
      <ModalAlert
        testId="businesses-table__note-failure-modal-alert"
        type="danger"
        title="Note could not be added. Please try again."
        hideModal={() => this.setState({ showNoteNotAddedAlert: false })}
      />
    );
  }

  renderBackendErrorModal(heading) {
    return (
      <ErrorModal
        heading={heading}
        hideModal={() => (this.store.cardHoldersBackendError = '')}
        buttonText="Dismiss"
      >
        If you believe this is an error, please contact support.
      </ErrorModal>
    );
  }

  render() {
    const { onRowClick, setRowLink } = this.props;
    const {
      currentList,
      activeSort,
      activeRow,
      setAttr,
      loading,
      updateKey,
      cardHoldersBackendError,
    } = this.store;
    const { tableKey, columns } = this.state;
    return (
      <div style={{ width: '100%' }} data-testid="business-table-wrapper">
        {cardHoldersBackendError && this.renderBackendErrorModal(cardHoldersBackendError)}
        {this.renderAddNoteModal()}
        {this.renderChangeStatusModal()}
        {this.renderNoteSuccessfullyAddedAlert()}
        {this.renderNoteNotAddedAlert()}
        {this.renderColEditTableHeader({
          filterOptions,
          filterProps: { type: 'small' },
          tableFilterTestId: 'businesses-table-header-buttons',
        })}
        <VSpacer />
        <LoadingOverlay active={loading}>
          <Table
            testId="businesses-table"
            loading={loading}
            key={tableKey}
            height={this.dynamicHeight}
            data={currentList}
            columns={columns}
            sort={activeSort}
            fixedColumnCount={1}
            rightAlignFixedColumns={true}
            activeRow={activeRow}
            onRowClick={onRowClick}
            setRowLink={setRowLink}
            onSort={(newSort) => setAttr('activeSort', newSort)}
            scrollToTopUpdateKey={updateKey}
            deferRenderTimeout={500}
            handleColSizeChange={this.handleColSizeChange.bind(this)}
            handleResetTable={this.clearFilterFunc}
          />
        </LoadingOverlay>
        {this.renderPagination()}
      </div>
    );
  }
}

BusinessesTable.propTypes = {
  autoHydrate: PropTypes.bool,
  handleRedirect: PropTypes.func,
  height: PropTypes.number,
  store: PropTypes.object,
  storeInitArgs: PropTypes.object,
  onRowClick: PropTypes.func,
  setRowLink: PropTypes.func,
  onStoreConstruction: PropTypes.func,
  onViewClick: PropTypes.func,
  userStore: PropTypes.object,
  localStorageKey: PropTypes.string,
};

BusinessesTable.defaultProps = {
  autoHydrate: true,
  handleRedirect: (store, tab) => {},
  height: 800,
  onRowClick: () => {},
  setRowLink: null,
  onStoreConstruction: null,
  onViewClick: PropTypes.func,
  store: null,
  storeInitArgs: { limit: 100 },
  userStore: {},
  localStorageKey: 'BusinessesTableConfig',
};

export default observer(BusinessesTable);
